import { Badge, IconButton, Popover } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useMemo, useCallback } from "react";
import { notificationService } from "service/notificationService";
import NotificationIcon from "../../assets/svg/notification.svg";
import {
    NotificationList,
    NotificationPopupHeader,
    NotificationTabs,
} from "./components/notificationPopup";
import { FilterPopup, FilterState } from "./components/notificationFilter";
import { NotificationItem, NotificationType } from "./types";
import { useNotificationLocalStorage } from "hooks/useNotificationLocalStorage";
import { useOrganisation } from "hooks/organization";
import { isApiError } from "service/baseService";
import { useAppContext } from "context/appContext";

// Styled components
const StyledPopover = styled(Popover)`
    & .MuiPaper-root {
        background-color: #2f3a51;
        border: none;
        border-radius: 8px;
        width: 700px;
    }
`;

const StyledBadge = styled(Badge)`
    & .MuiBadge-badge {
        background-color: #e86881;
        color: #ffffff;
        font-size: 12px;
        font-weight: 400;
        right: 2px;
        top: 8px;
    }
`;

const NotificationIconButton = styled(IconButton)`
    & img {
        width: 24px;
        height: 24px;
    }
`;

export type NotificationProps = {
    email: string;
};

export const Notification = (props: NotificationProps) => {
    const { email } = props;
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [filterAnchorEl, setFilterAnchorEl] =
        React.useState<null | HTMLElement>(null);
    const [notifications, setNotifications] = React.useState<
        NotificationItem[]
    >([]);
    const [selectedTab, setSelectedTab] =
        React.useState<NotificationType>("all");
    const [appliedFilters, setAppliedFilters] = React.useState<FilterState>({
        search: "",
        unreadOnly: false,
        selectedOrganizations: [],
    });
    const open = Boolean(anchorEl);
    const filterOpen = Boolean(filterAnchorEl);
    const { isNotificationRead } = useNotificationLocalStorage();
    const orgList = useOrganisation().orgList;
    const {
        storeData: {
            user: { userData },
        },
    } = useAppContext();
    const personalAccountLabel =
        `${userData.givenName} ${userData.familyName}`.trim() || email;

    useEffect(() => {
        if (!email) {
            return;
        }
        let intervalId: NodeJS.Timeout;
        let isActive = true;

        const fetchNotifications = async () => {
            try {
                const response = await notificationService.getNotifications(
                    email
                );

                if (isApiError(response)) {
                    throw new Error(response.error.message);
                }

                // Only update state if component is still mounted
                if (isActive) {
                    setNotifications(response.data);
                }
            } catch (error) {
                if (isActive) {
                    console.error("Failed to fetch notifications:", error);
                }
            }
        };

        // Initial fetch
        fetchNotifications();

        // Set up polling interval
        intervalId = setInterval(fetchNotifications, 60000);

        // Cleanup function
        return () => {
            isActive = false;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [email]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        // Reset filters when main notification popup closes
        setAppliedFilters({
            search: "",
            unreadOnly: false,
            selectedOrganizations: [],
        });
    };

    const handleFilterClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setFilterAnchorEl(event.currentTarget);
        },
        []
    );

    const handleFilterClose = useCallback(() => {
        setFilterAnchorEl(null);
    }, []);

    const handleApplyFilter = useCallback((filters: FilterState) => {
        setAppliedFilters(filters);
    }, []);

    const handleTabChange = (
        _event: React.SyntheticEvent,
        newValue: NotificationType
    ) => {
        setSelectedTab(newValue);
    };

    // Memoize filtered notifications to avoid recalculating on every render
    const filteredNotifications = useMemo(() => {
        // Apply filters to notifications
        const applyFilters = (notificationList: NotificationItem[]) => {
            let filtered = notificationList;

            // Apply search filter
            if (appliedFilters.search) {
                filtered = filtered.filter(
                    (notification) =>
                        notification.title
                            .toLowerCase()
                            .includes(appliedFilters.search.toLowerCase()) ||
                        notification.message
                            .toLowerCase()
                            .includes(appliedFilters.search.toLowerCase())
                );
            }

            // Apply unread filter
            if (appliedFilters.unreadOnly) {
                filtered = filtered.filter(
                    (notification) => !isNotificationRead(notification.id)
                );
            }

            // Apply organization filter
            if (appliedFilters.selectedOrganizations.length > 0) {
                filtered = filtered.filter((notification) =>
                    appliedFilters.selectedOrganizations.includes(
                        notification.orgId
                    )
                );
            }

            return filtered;
        };

        const sensorAlerts = notifications.filter(
            (notification) => notification.type === "sensor"
        );
        const downloads = notifications.filter(
            (notification) => notification.type === "download"
        );

        const events = notifications.filter(
            (notification) => notification.type === "event"
        );

        return {
            all: applyFilters(notifications),
            sensor: applyFilters(sensorAlerts),
            download: applyFilters(downloads),
            event: applyFilters(events),
        };
    }, [notifications, appliedFilters, isNotificationRead]);

    const unreadCount = notifications.filter(
        (n) => !isNotificationRead(n.id)
    ).length;

    // Check if there are active filters
    const hasActiveFilters = useMemo(() => {
        return (
            appliedFilters.search !== "" ||
            appliedFilters.unreadOnly ||
            appliedFilters.selectedOrganizations.length > 0
        );
    }, [appliedFilters]);

    // Get organizations Map (UUID -> name) for display
    const organizationsMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const org of orgList) {
            map.set(org.uuid, org.name);
        }
        return map;
    }, [orgList]);

    const getCurrentNotifications = () => {
        return selectedTab === "all"
            ? filteredNotifications.all
            : filteredNotifications[selectedTab];
    };

    return (
        <li className="nav-item no-arrow">
            <div className="nav-link">
                <StyledBadge badgeContent={unreadCount} color="error">
                    <NotificationIconButton
                        id="notification-button"
                        aria-controls={open ? "notification-popup" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                    >
                        <img src={NotificationIcon} alt="notification" />
                    </NotificationIconButton>
                </StyledBadge>
            </div>

            <StyledPopover
                id="notification-popup"
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <NotificationPopupHeader
                    onFilterClick={handleFilterClick}
                    hasActiveFilters={hasActiveFilters}
                />

                <NotificationTabs
                    selectedTab={selectedTab}
                    onTabChange={handleTabChange}
                    tabCounts={{
                        all: filteredNotifications.all.length,
                        sensor: filteredNotifications["sensor"].length,
                        download: filteredNotifications.download.length,
                        event: filteredNotifications["event"].length,
                    }}
                />

                <NotificationList
                    notifications={getCurrentNotifications()}
                    selectedTab={selectedTab}
                    organizationsMap={organizationsMap}
                />
            </StyledPopover>

            <FilterPopup
                open={filterOpen}
                anchorEl={filterAnchorEl}
                onClose={handleFilterClose}
                onApplyFilter={handleApplyFilter}
                orgList={orgList}
                userEmail={userData.email || email}
                personalAccountLabel={personalAccountLabel}
                currentFilters={appliedFilters}
            />
        </li>
    );
};
